/* @flow */

// NOTE: ramda does not have an equivalent of _.merge, unfortunately
import { fromJS } from 'immutable';
import _mergeDeep from 'lodash.merge';
import path from 'ramda/src/path';
import compose from 'ramda/src/compose';
import tap from 'ramda/src/tap';
import { toJS } from 'trustar/utils/utils.js';
import { IO } from 'trustar/fp';
import createDebugger from 'debug';

const debug = createDebugger('lib:storage'); // eslint-disable-line no-unused-vars

const LOCAL_STORAGE_KEY = 'TS_STATION';

const getStorage = () => {
  try {
    const item = localStorage.getItem(LOCAL_STORAGE_KEY) || '';
    return JSON.parse(item) || {};
  } catch (err) {
    return {};
  }
};

const setValueByArray = (storage: Object, keypath: string[], value: any): any => {
  if (keypath.length === 1) {
    storage[keypath[0]] = value;
    return storage;
  } else {
    const key = keypath.shift();
    if (!storage[key]) storage[key] = {};
    return setValueByArray(storage[key], keypath, value);
  }
};

const removeValueByArray = (storage: Object, keypath: string[]): any => {
  if (keypath.length === 1) {
    delete storage[keypath[0]];
    return storage;
  } else {
    const key = keypath.shift();
    if (!storage[key]) storage[key] = {};
    return removeValueByArray(storage[key], keypath);
  }
};

const mergeValueByArray = (storage: any, keypath: any, value: any): any => {
  if (keypath.length === 1) {
    storage[keypath[0]] = _mergeDeep(storage[keypath[0]], value);
    return storage;
  } else {
    const key = keypath.shift();
    if (!storage[key]) storage[key] = {};
    return mergeValueByArray(storage[key], keypath, value);
  }
};

export const has = (key: string): boolean => {
  const storage = getStorage();
  return storage && storage.hasOwnProperty(key);
};

export const hasIn = (keypath: string[]): boolean => {
  try {
    const storage = getStorage();
    const value = path(keypath, storage);
    return value !== undefined;
  } catch (err) {
    return false;
  }
};

export const get = (key: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    try {
      const storage = getStorage();
      resolve(storage[key]);
    } catch (err) {
      reject(err);
    }
  });
};

// Currently supporting only 2 levels deep; i.e. key='key1.key2'
export const getIn = (keypath: string[]): Promise<any> => {
  return new Promise((resolve, reject) => {
    try {
      const storage = getStorage();
      const value = path(keypath, storage);
      resolve(value);
    } catch (err) {
      reject(err);
    }
  });
};

export const set = (key: string, value: any): Promise<any> => {
  return new Promise((resolve, reject) => {
    try {
      const storage = getStorage();
      storage[key] = value;
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(storage));
      resolve(storage);
    } catch (err) {
      reject(err);
    }
  });
};

export const setIn = (keypath: string[], value: any): Promise<any> => {
  return new Promise((resolve, reject) => {
    try {
      const storage = getStorage();
      setValueByArray(storage, keypath, value);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(storage));
      resolve(storage);
    } catch (err) {
      reject(err);
    }
  });
};

export const remove = (key: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    try {
      const storage = getStorage();
      delete storage[key];
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(storage));
      resolve();
    } catch (err) {
      reject(err);
    }
  });
};

// TODO: This promise is entirely uneccessary. The only reason we promise
// everything here is because initially I wanted a way to represent disjunction
// in a more functional way... but I didn't know about monads.
export const removeIn = (keypath: string[]): Promise<any> => {
  return new Promise((resolve, reject) => {

    // TODO: We should use IO for all the logic in this file
    // TODO: Converting back and forth between immutable and a JSON string seems
    // like a great use case for an isomorphism
    // NOTE: we do the map-tap combo simply to do a side effect. Might be worth
    // considering adding a `do` method to IO
    IO(() => localStorage.getItem(LOCAL_STORAGE_KEY))
      .map(compose(fromJS, JSON.parse))
      .map(data => data.removeIn(keypath)) // Transform back into JS object to stringify
      .map(compose(JSON.stringify, toJS))
      .map(tap(str => localStorage.setItem(LOCAL_STORAGE_KEY, str))) // See NOTE
      .fold(reject, resolve);
  });
};

export const mergeDeep = (key: string, value: any): Promise<any> => {
  return new Promise((resolve, reject) => {
    try {
      const storage = getStorage();
      storage[key] = _mergeDeep(storage[key], value);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(storage));
      resolve();
    } catch (err) {
      reject(err);
    }
  });
};

export const mergeDeepIn = (keypath: string[], value: any): Promise<any> => {
  return new Promise((resolve, reject) => {
    try {
      const storage = getStorage();
      mergeValueByArray(storage, keypath, value);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(storage));
      resolve(storage);
    } catch (err) {
      reject(err);
    }
  });
};



// WEBPACK FOOTER //
// ./web_modules/trustar/utils/storage.js